/** @jsxImportSource react */
import React from "react";
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/auth';
import { UserRole } from '../types';
import { Menu } from 'lucide-react';
import { ChevronDownIcon } from '../constants';
import { useTranslation } from '../i18n';
import { NotificationsPanel } from './NotificationsPanel';
import LanguageSwitcher from './LanguageSwitcher';

// Re-integrated UserMenu component to maintain profile/logout functionality
const UserMenu = () => {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/');
    }

    const welcomeName = (user?.role === UserRole.Client && user.teCode)
        ? `${user.name} (${user.teCode})`
        : user?.name || '';
    
    return (
        <div className="relative" ref={menuRef}>
            <button 
                onClick={() => setIsOpen(prev => !prev)}
                className="flex items-center gap-2 text-secondary-800 hover:bg-background-off p-2 rounded-lg"
            >
                <span className="font-semibold text-sm hidden sm:inline">{welcomeName}</span>
                <div className="h-8 w-8 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold">
                    {user?.name ? user.name[0].toUpperCase() : 'U'}
                </div>
                <ChevronDownIcon />
            </button>
            {isOpen && (
                <div className="absolute end-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-50 overflow-hidden border">
                    <Link to="/profile" onClick={() => setIsOpen(false)} className="block px-4 py-2 text-sm text-secondary-700 hover:bg-background-off">{t('profile')}</Link>
                    <button onClick={handleLogout} className="w-full text-start px-4 py-2 text-sm text-rose-600 hover:bg-rose-50">{t('logout')}</button>
                </div>
            )}
        </div>
    );
};


// Merged Header component with new styles and existing functionality
const Header = ({ toggleMobileSidebar, pageTitle }: { toggleMobileSidebar: () => void; pageTitle: string }) => {
    const { t } = useTranslation();

    return (
        <header className="bg-white sticky top-0 z-30 border-b border-secondary-100 shadow-sm">
            <div className="px-4 h-16 flex items-center justify-between">
                <div className="flex items-center">
                    <button
                        onClick={toggleMobileSidebar}
                        className="p-2 rounded-lg md:hidden text-secondary-500 hover:text-primary-600 hover:bg-background-off transition-colors"
                        aria-label={t('toggleMenu')}
                    >
                        <Menu size={22} />
                    </button>
                    <h1 className="text-xl font-semibold text-secondary-800 ms-3 md:ms-0">
                        {pageTitle}
                    </h1>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                    <NotificationsPanel />
                    <LanguageSwitcher />
                    <UserMenu />
                </div>
            </div>
        </header>
    );
}

export default Header;